import { useAuthStore } from '@/stores/authStore'

/**
 * fetch() wrapper that attaches the current Cognito idToken and self-heals a
 * transiently-expired token.
 *
 * The idToken expires ~hourly. The `_authenticated` route refreshes it on a
 * 4-minute interval and on tab focus, but a request can still fire in the gap
 * with a just-expired token. Before this wrapper, that surfaced as a hard 401:
 * `/api/events` returned "Unauthorized" (events vanished from the map/list) and
 * the auth tree could thrash (issue #341). Here, a 401 forces a single token
 * refresh (Amplify's getSession mints a fresh idToken from the 30-day refresh
 * token) and retries once, so a stale token heals instead of failing the user.
 *
 * Returns the raw Response so callers keep their existing `.ok` / `.json()`
 * handling. `credentials: 'include'` is always set (CF Access cookie).
 */
export async function authedFetch(
  input: string,
  init: RequestInit = {},
): Promise<Response> {
  const withAuth = (token: string | null): RequestInit => ({
    ...init,
    credentials: 'include',
    headers: {
      ...(init.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })

  let token = useAuthStore.getState().idToken
  let res = await fetch(input, withAuth(token))

  if (res.status === 401) {
    // Token likely expired between refresh ticks — force a refresh and retry once.
    await useAuthStore.getState().refreshToken()
    const fresh = useAuthStore.getState().idToken
    if (fresh && fresh !== token) {
      token = fresh
      res = await fetch(input, withAuth(token))
    }
  }

  return res
}
